import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getConfig } from '../config/getConfig';
import { TokenManager } from '../utils/tokenManager';
import { toast } from 'react-toastify';
import { isDemoMode, mockPubKeyList, mockManageLog, mockEncDecLog, mockSummaryCounts, mockDataSize, mockRecentlyEncDec, mockAlgoComp, mockEncKeyComp, mockKeyTypeComp, mockSixMonthPay } from '../data/demoMockData';

// 런타임 설정 가져오기
const config = getConfig();

// 기본 설정으로 config에서 가져온 API URL 사용
const api = axios.create({
    baseURL: config.API_URL
});

// 토큰 발급 및 재발급용 인스턴스 (config에서 가져온 Auth URL 사용)
const authApi = axios.create({
    baseURL: config.AUTH_URL
});

// 서버 상태 확인 함수
const checkServerStatus = async (serverUrls) => {
    console.log('서버 상태 확인 시작...');

    const statusResults = {};

    for (const [name, url] of Object.entries(serverUrls)) {
        try {
            const response = await fetch(url, {
                method: 'GET',
                timeout: 5000 // 5초 타임아웃
            });
            statusResults[name] = response.ok ? '정상' : `오류 (${response.status})`;
        } catch (error) {
            statusResults[name] = '연결 실패';
        }
    }

    // 서버 상태 결과 알림
    const statusMessage = Object.entries(statusResults)
        .map(([name, status]) => `${name.toUpperCase()}: ${status}`)
        .join('\n');

    console.log('서버 상태 확인 결과:', statusResults);

    // 모든 서버가 다운된 경우
    const allDown = Object.values(statusResults).every(status => status.includes('❌'));
    if (allDown) {
        toast.error(`모든 서버가 다운되었습니다.\n\n${statusMessage}\n\n관리자에게 문의하세요.`, {
            autoClose: false, // 자동으로 닫히지 않음
            closeOnClick: false
        });
    } else {
        toast.warn(`일부 서버에 문제가 있습니다:\n\n${statusMessage}`, {
            autoClose: 8000
        });
    }
};

// 토큰 오류 처리 함수
const handleTokenError = (error) => {
    switch (error.message) {
        case 'SA_COMMON_NO_SITE_TOKEN':
            toast.error('고객사 관리 모드에서 활동하려면 고객사를 선택해주세요.');
            window.location.href = '/safedb/SA/adminSites';
            break;

        case 'SA_AREA_WITH_SITE_TOKEN':
            toast.error('WARNING! 관리자 권한 영역입니다. 고객사 모드 강제 해제 완료. (siteToken 제거됨)');
            // siteToken은 이미 TokenManager에서 제거됨
            break;

        case 'INVALID_TOKEN':
        case 'NO_VALID_TOKEN':
            toast.error('인증이 만료되었습니다. 다시 로그인해주세요.');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('refreshToken');
            sessionStorage.removeItem('siteToken');
            window.location.href = '/authentication/simple/login';
            break;

        default:
            break;
    }
};

// 강제 전체 로그아웃 후 로그인 화면으로 이동 (SSO 버튼 노출)
const forceLogoutToLogin = (message = '유효하지 않은 접근입니다. 다시 로그인해 주세요.', reason = 'forced_logout') => {
    try { toast.error(message); } catch (e) { /* no-op */ }
    try {
        // 세션/복원 키 전부 정리
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('siteToken');
        sessionStorage.removeItem('checkorg');
        sessionStorage.removeItem('checkorg_companyId');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('oidc_return_url');
        sessionStorage.removeItem('oidc_restore_siteToken');
        sessionStorage.removeItem('oidc_restore_checkorg');
        sessionStorage.removeItem('oidc_restore_checkorg_companyId');
        sessionStorage.setItem('auth_message', message);
    } catch (e) { /* no-op */ }
    try {
        // OIDC 로컬 토큰 정리 (호환 키 포함)
        localStorage.removeItem('oidc_access_token');
        localStorage.removeItem('oidc_refresh_token');
        localStorage.removeItem('oidc_id_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('id_token');
    } catch (e) { /* no-op */ }
    setTimeout(() => {
        window.location.href = `/authentication/simple/login?reauth=1&reason=${encodeURIComponent(reason)}`;
    }, 50);
};

// JWT에서 사용자명(sub/username/user_id) 추출
const extractUsernameFromJwt = (jwt) => {
    try {
        const payloadPart = jwt.split('.')[1];
        const decoded = JSON.parse(atob(payloadPart));
        return decoded.sub || decoded.username || decoded.user_id || null;
    } catch (e) {
        return null;
    }
};

// OIDC Access Token에서 사용자명(preferred_username/sub) 추출
const extractUsernameFromOidcAccessToken = (accessToken) => {
    try {
        const payloadPart = accessToken.split('.')[1];
        const decoded = JSON.parse(atob(payloadPart));
        return decoded.preferred_username || decoded.sub || decoded.username || decoded.user_id || null;
    } catch (e) {
        return null;
    }
};

// 중요한 작업 감지 로직은 백엔드 RequestInterceptor로 이관됨
// 프론트엔드에서는 401 응답 시 자동 재인증만 처리

// 시스템 관리자 토큰인지 확인 (SSO 우회 로그인)
// cou가 "initech0"이고 role이 "SA"이면 시스템 관리자 토큰
const isSystemAdminToken = (token) => {
    try {
        if (!token) return false;
        const payloadPart = token.split('.')[1];
        const decoded = JSON.parse(atob(payloadPart));
        // 시스템 관리자 토큰 판별:
        // 1. 원본 SA 토큰: cou가 initech0이고 role이 SA
        // 2. SA의 Impersonation 토큰: sa_imp가 true (시스템 관리자의 고객사 모드)
        return (decoded.cou === 'initech0' && decoded.role === 'SA') || decoded.sa_imp === true;
    } catch (e) {
        return false;
    }
};

// 현재 사용 중인 토큰(token 또는 siteToken)이 시스템 관리자 토큰인지 확인
const isCurrentUserSystemAdmin = () => {
    const siteToken = sessionStorage.getItem('siteToken');
    const token = sessionStorage.getItem('token');
    // siteToken이 있으면 siteToken 확인, 없으면 일반 token 확인
    return isSystemAdminToken(siteToken) || isSystemAdminToken(token);
};

// OIDC 토큰 만료 확인
const isOidcTokenExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expTime = payload.exp * 1000;
        const currentTime = Date.now();
        const isExpired = expTime < currentTime;

        console.log(`🕐 OIDC Token 만료 체크:`, {
            exp: new Date(expTime).toISOString(),
            now: new Date(currentTime).toISOString(),
            isExpired: isExpired,
            timeLeft: isExpired ? 0 : Math.round((expTime - currentTime) / 1000) + 's'
        });

        return isExpired;
    } catch (error) {
        console.error('🚨 OIDC Token 파싱 에러:', error);
        return true;
    }
};

// OIDC 재인증 진행 중 플래그
let oidcReauthInProgress = false;

// OIDC 토큰 사용 추적 제거 (여러 API 호출에서 동일한 토큰 사용 허용)

// 중복 요청 방지를 위한 진행 중인 요청 추적
const ongoingRequests = new Map();

// 재인증 플래그 리셋 함수 (외부에서 호출 가능)
export const resetOidcReauthFlag = () => {
    console.log('OIDC 재인증 플래그 리셋');
    oidcReauthInProgress = false;
    // OIDC 토큰 사용 플래그 제거됨
    // 진행 중인 요청도 모두 정리
    ongoingRequests.clear();
};

// OIDC 재인증 리다이렉트
const redirectToOidcLogin = () => {
    // 이미 재인증이 진행 중이면 중복 리다이렉트 방지
    if (oidcReauthInProgress) {
        console.warn('OIDC re-authentication already in progress, skipping');
        return;
    }

    oidcReauthInProgress = true;
    // 런타임 설정 우선 적용
    const { SSO_KEYCLOAK_URL, SSO_REALM, SSO_CLIENT_ID } = getConfig();
    const keycloakUrl = SSO_KEYCLOAK_URL;
    const realm = SSO_REALM;
    const clientId = SSO_CLIENT_ID;
    // 🔧 깨끗한 URL 생성 (키클락 파라미터 제거)
    let cleanUrl = window.location.origin + window.location.pathname;

    // 기존 키클락 파라미터들 제거
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('session_state');
    urlParams.delete('iss');
    urlParams.delete('code');

    // 남은 파라미터가 있으면 추가
    const remainingParams = urlParams.toString();
    if (remainingParams) {
        cleanUrl += '?' + remainingParams;
    }

    const redirectUri = encodeURIComponent(cleanUrl);

    const loginUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `scope=openid%20profile%20email&` +
        `prompt=login&` +
        `max_age=0`; // 강제 재로그인

    console.log('Critical operation requires fresh OIDC authentication, redirecting to clean URL:', cleanUrl);

    // 약간의 지연 후 리다이렉트 (다른 API 호출들이 정리될 시간 제공)
    setTimeout(() => {
        window.location.href = loginUrl;
    }, 100);
};

// 적절한 토큰 헤더 가져오기 함수 (OIDC + SafeDB JWT 하이브리드)
const getTokenHeaders = (apiPath = '', forceFreshOidc = false) => {
    try {
        const siteToken = sessionStorage.getItem('siteToken');
        const safedbJwtToken = sessionStorage.getItem('token');
        const oidcToken = localStorage.getItem('oidc_access_token');

        // 강제 OIDC 요청 시만 (인터셉터에서 critical operation은 자동 처리)
        if (forceFreshOidc) {
            if (!oidcToken) {
                console.log('🔒 Force OIDC requested but no token found');
                return null;
            }

            console.log(`🔄 Force OIDC token usage for: ${apiPath}`);
            return {
                Authorization: `Bearer ${oidcToken}`,
                'X-Auth-Type': 'oidc-forced'
            };
        }

        // 공용(고객사) 영역 호출 시 TokenManager의 스마트 로직 사용
        if (apiPath && apiPath.includes('/safedb/common/')) {
            try {
                // TokenManager의 getAppropriateToken 사용 (ADMIN은 일반 토큰, SA는 사이트 토큰)
                const tokenResult = TokenManager.getAppropriateToken(apiPath);
                const authType = tokenResult.type === 'siteToken' ? 'site-token' : 'refresh-token';

                console.log(`🔑 Common API 토큰 선택: ${tokenResult.type} (${TokenManager.getUserRole()} 사용자)`);

                return {
                    Authorization: `Bearer ${tokenResult.token}`,
                    'X-Auth-Type': authType
                };
            } catch (error) {
                // TokenManager에서 적절한 에러 처리 (SA_COMMON_NO_SITE_TOKEN 등)
                console.warn('TokenManager 토큰 선택 실패:', error.message);
                throw error; // 라우트 가드가 처리하도록 에러 전파
            }
        }

        // SA/일반 영역: SafeDB JWT 우선 (RefreshToken 기반)
        if (safedbJwtToken) {
            return {
                Authorization: `Bearer ${safedbJwtToken}`,
                'X-Auth-Type': 'refresh-token'  // 백엔드에서 구분용
            };
        }

        // OIDC 토큰 fallback (직접 사용 - 호환성용)
        if (oidcToken) {
            return {
                Authorization: `Bearer ${oidcToken}`,
                'X-Auth-Type': 'oidc-fallback'
            };
        }

        // 토큰이 없으면 로그인 페이지로 리다이렉트
        console.warn('No valid token found, redirecting to login');
        window.location.href = '/authentication/simple/login';
        return null;
    } catch (error) {
        console.error('Token header creation error:', error);
        handleTokenError(error);
        return null;
    }
};

// 로그인 API 호출 함수
export const loginUser = async ({ username, password }) => {
    try {
        const response = await authApi.post('/auth/login', { username, password });
        return response.data; // 토큰 및 리프레시 토큰을 반환
    } catch (error) {
        console.error('로그인 요청 에러');
        throw error;
    }
};

// 시스템 관리자 로그인 API 호출 함수 (Keycloak 우회)
export const systemAdminLogin = async ({ username, password }) => {
    try {
        const response = await authApi.post('/auth/sa/login', { username, password });
        return response.data; // 토큰 및 사용자 정보 반환
    } catch (error) {
        console.error('개발용 로그인 요청 에러');
        throw error;
    }
};

// 가입 신청 시 인증번호 보내는 API
export const sendAuthCode = async ({ applyName, applyTel, applyEmail, companyName }) => {
    try {
        const response = await api.post('/apply/sendAuthCode', {
            applyName,
            applyTel,
            applyEmail,
            companyName,
        });
        return response.data; // {boolean} 타입의 응답을 반환
    } catch (error) {
        console.error('가입 신청 에러:');
        throw error;
    }
};

export const reSendAuthCode = async ({ applyEmail }) => {
    try {
        const response = await api.post('/apply/sendAuthCode', {
            applyEmail
        });
        return response.data; // {boolean} 타입의 응답을 반환
    } catch (error) {
        console.error('가입 신청 에러:');
        throw error;
    }
};

// 가입 신청 시 유효 인증코드 확인 API
export const checkAuthCode = async ({ email, code }) => {
    try {
        const response = await api.post('/apply/checkAuthCode', {
            email,
            code
        });
        return response.data; // {boolean} 타입의 응답을 반환
    } catch (error) {
        console.error('유효 인증코드 확인 에러:');
        throw error;
    }
};

// 이미지 업로드 API
export const ImagesUpload = async (imageFile, type) => {
    const headers = getTokenHeaders();
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        // FormData 객체 생성
        const formData = new FormData();
        formData.append('type', type); // 'manager' 또는 'company'
        formData.append('image', imageFile); // 업로드할 이미지 파일

        // 요청 설정
        const response = await api.post('/images/upload', formData, {
            headers: {
                ...headers,
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        // 에러 처리

        if (error.response) {
            console.error('CALL APIs 응답 데이터:');
        }
        throw error;
    }
};

// 관리자의 사이트 이미지 업로드  / 고객사의 이미지 업로드 API
export const ImagesUpload_Site = async (imageFile, type) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        // FormData 객체 생성
        const formData = new FormData();
        formData.append('type', type); // 'manager' 또는 'company'
        formData.append('image', imageFile); // 업로드할 이미지 파일

        // 요청 설정
        const response = await api.post('/images/upload', formData, {
            headers: {
                ...headers,
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    } catch (error) {
        // 에러 처리

        if (error.response) {
            console.error('CALL APIs 응답 데이터:');
        }
        throw error;
    }
};

// 이미지 조회 API (UUID 기반) - 범용 사용
export const fetchImageByUuid = async (imgUuid) => {
    // 현재 경로에 따라 적절한 토큰 헤더 선택
    const currentPath = window.location.pathname;
    let headers;

    if (currentPath.includes('/safedb/SA/')) {
        // SA 영역에서는 일반 토큰 사용
        headers = getTokenHeaders('/safedb/SA/');
    } else if (currentPath.includes('/safedb/common/')) {
        // 공용 영역에서는 공용 토큰 사용
        headers = getTokenHeaders('/safedb/common/');
    } else {
        // 기본적으로 일반 토큰 사용 (SA 권한)
        headers = getTokenHeaders();
    }

    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null;
    }

    try {
        const response = await api.get(`/images/${imgUuid}`, {
            headers,
            responseType: 'blob' // 이미지 데이터를 blob으로 받기
        });

        // Blob을 URL로 변환
        const imageBlob = response.data;
        const imageUrl = URL.createObjectURL(imageBlob);

        return imageUrl;
    } catch (error) {
        console.error('이미지 조회 에러:', error);
        throw error;
    }
};

// 회사 목록 조회 API 
export const fetchCompanyList = async (page = 0, size = 10) => {
    const headers = getTokenHeaders('/safedb/SA/companies');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null;
    }

    try {
        const params = { page, size };
        if (isDemoMode()) {
            params.demo = true;
        }
        const response = await api.get('/companies', {
            params,
            headers
        });
        return response.data;
    } catch (error) {
        console.error('회사 목록 조회 에러:');
        throw error;
    }
};

// 회사 생성 API
export const createCompany = async (data) => {
    const headers = getTokenHeaders('/safedb/SA/companies');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.post(`/companies`, data, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('회사 생성 에러:');
        throw error;
    }
};

// 회사 정보 변경 API
export const updateCompanyInfo = async (companyId, data) => {
    const headers = getTokenHeaders('/safedb/SA/companies');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.put(`/companies/${companyId}`, data, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('회사 정보 업데이트 에러:');
        throw error;
    }
};
// 회사 삭제 API
export const deleteCompanyInfo = async (companyId) => {
    const headers = getTokenHeaders('/safedb/SA/companies');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.delete(`/companies/${companyId}`, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('회사 정보 삭제 에러:');
        throw error;
    }
};
// 회사 다중 삭제 API
export const deleteMultiCompanyInfo = async (companyIds) => {
    try {
        const response = await api.delete(`/companies`, {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('token')}` // 토큰을 세션 스토리지에서 가져옴
            },
            data: { companyIds }, // 요청 본문에 `companyIds` 전달

        });

        return response.data;
    } catch (error) {
        console.error('회사 정보 삭제 에러:');
        throw error;
    }
};

// 회사 조회 API 
export const fetchCompanyInfo = async (companyId) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get(`/companies/${companyId}`, {

            headers

        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('관리자 계정 목록 조회 에러:');
        throw error;
    }
};


// 고객사 요금정책 변경 API
export const updateCompanyPricingPolicy = async (companyId, oldPricingPolicyUuid, newPricingPolicyUuid) => {
    const headers = getTokenHeaders('/safedb/SA/pricing');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.put(`/companies/${companyId}/pricingPolicy`, {
            oldPricingPolicyUuid,
            newPricingPolicyUuid,
            companyId
        }, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('고객사 요금정책 변경 에러:', error);
        throw error;
    }
};

// 관리자 계정 목록 조회 API  (관리자)
export const fetchAdminList = async (page = 0, size = 10) => {
    const headers = getTokenHeaders('/safedb/SA/users');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/users', {
            params: { page, size },
            headers
        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('관리자 계정 목록 조회 에러:');
        throw error;
    }
};

// 관리자 계정 목록 조회 API  (고객사)
export const fetchAdminList_Site = async (page = 0, size = 10) => {

    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }


    try {
        const response = await api.get('/users', {
            params: { page, size },
            headers

        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('관리자 계정 목록 조회 에러:');
        throw error;
    }
};


// 관리자 삭제 API
export const deleteAdminInfo = async (userUuid) => {
    const headers = {};

    const token = sessionStorage.getItem('token');

    headers.Authorization = `Bearer ${token}`;
    // }

    try {
        const response = await api.delete(`/users/${userUuid}`, {
            headers
        });
        if (response.status === 204) {
            return { success: true }; // 성공 응답 반환
        }
        return response.data;
    } catch (error) {
        console.error('관리자 정보 삭제 에러:');
        throw error;
    }
};
// 관리자 다중 삭제 API
export const deleteMultiAdminInfo = async (userUuids) => {
    const headers = {};

    const token = sessionStorage.getItem('token');

    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.delete(`/users`, {
            headers,
            data: { userUuids }, // 요청 본문에 `companyIds` 전달

        });
        if (response.status === 204) {
            return { success: true }; // 성공 응답 반환
        }
        return response.data;
    } catch (error) {
        console.error('회사 정보 삭제 에러:');
        throw error;
    }
};

// 관리자 삭제 API_Site (고객사 관리자 삭제)
export const deleteAdminInfo_Site = async (userUuid) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.delete(`/users/${userUuid}`, {
            headers
        });
        if (response.status === 204) {
            return { success: true }; // 성공 응답 반환
        }
        return response.data;
    } catch (error) {
        console.error('관리자 정보 삭제 에러:');
        throw error;
    }
};
// 관리자 다중 삭제 API_Site
export const deleteMultiAdminInfo_Site = async (userUuids) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.delete(`/users`, {
            headers,
            data: { userUuids }, // 요청 본문에 `companyIds` 전달

        });
        if (response.status === 204) {
            return { success: true }; // 성공 응답 반환
        }
        return response.data;
    } catch (error) {
        console.error('회사 정보 삭제 에러:');
        throw error;
    }
};


// 로그인 정보 조회 API 
export const getAdminInfo = async () => {
    const headers = getTokenHeaders('/safedb/SA/users');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/users/login', {
            headers
        });
        return { adminInfo: response.data, isPasswordChangeNeeded: false, changeReasonCode: null };
    } catch (error) {
        if (error.response && (error.response.data.code === -5 || error.response.data.code === -6)) {
            // 비밀번호 만료 에러 -5, 비밀번호 변경 필요 -6
            const changeReasonCode = error.response.data.code;
            return { adminInfo: null, isPasswordChangeNeeded: true, changeReasonCode }; // 비밀번호 변경 필요 표시와 원인 코드 반환
        }

        // 기타 에러가 발생한 경우 에러를 던짐
        return { adminInfo: null, isPasswordChangeNeeded: false, error };
    }
};

// 관리자 정보 변경 API
export const updateAdminInfo = async (userUuid, data) => {
    const headers = getTokenHeaders('/safedb/SA/users');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.put(`/users/${userUuid}`, data, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('관리자 정보 업데이트 에러:');
        throw error;
    }
};

// 관리자 계정 정보 변경 시 회사 리스트 호출 API
export const getCompanyList = async () => {
    try {
        const response = await api.get('/companies/list', {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('token')}` // 토큰을 세션 스토리지에서 가져와 헤더에 포함
            }

        });

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("회사 리스트 호출 에러 발생");
        throw error;
    }
};

// 관리자 등록 API
export const addAdminUsers = async (data) => {
    const headers = getTokenHeaders('/safedb/SA/users');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.post(`/users`, data, {
            headers
        });

        return response;
    } catch (error) {
        console.log("관리자 등록 에러 발생");
        throw error;
    }
};

// 관리자 정보 변경 API (SITE - 고객사 관리자 정보 변경)
export const updateAdminInfo_Site = async (userUuid, data) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.put(`/users/${userUuid}`, data, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('관리자 정보 업데이트 에러:');
        throw error;
    }
};

// 고객사 토큰 발급 API 호출 함수
export const requestSiteToken = async ({ username, refreshToken, companyId }) => {
    // 중요한 작업: 사이트 토큰 발급은 OIDC 재인증 필요
    const headers = getTokenHeaders('/safedb/SA/adminSites');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        throw new Error('AUTHENTICATION_REQUIRED'); // 명시적 에러 발생
    }

    try {
        const response = await authApi.post('/auth/impersonation', {
            username,
            refreshToken,
            companyId
        }, {
            headers
        });

        // OIDC 토큰은 인터셉터에서 관리됨 (사이트 토큰 발급 시 제거하지 않음)

        return response.data; // 발급된 토큰 반환

    } catch (error) {
        console.log("고객사 토큰 발급 API 에러 발생");

        // OIDC 토큰은 인터셉터에서 관리됨 (에러 시에도 제거하지 않음)

        throw error;
    }
};

// 비밀번호 변경 API
export const changePassword = async (data) => {
    try {
        const response = await api.put(`/users/password`, data, {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('token')}` // 토큰을 세션 스토리지에서 가져옴
            }
        });
        if (response.status === 204) {
            return { success: true }; // 성공 응답 반환
        }
        return response.data;

    } catch (error) {
        console.log("비밀번호 변경 API 에러 발생");
        throw error;
    }
};

// 비밀번호 변경 API for 관리자
export const changePassword_forAdmin = async (userUuid, data) => {

    const headers = {};

    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.put(`/users/${userUuid}/password`, data, {
            headers
        });
        if (response.status === 204) {
            return { success: true }; // 성공 응답 반환
        }
        return response.status;
    } catch (error) {
        console.log("관리자 비밀번호 변경 에러 발생");
        throw error;
    }
};

// // API KEY 목록 조회 API 
export const fetchApiKeyList = async (page = 0, size = 10) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        console.log('Fetching API key list with headers:', headers);
        const response = await api.get('/apikeys', {
            params: { page, size },
            headers
        });

        console.log('API response:', response);

        // 응답 구조 검증
        if (!response || !response.data) {
            throw new Error('Invalid API response structure');
        }

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('API Key 목록 조회 에러:', error);

        // 네트워크 에러 처리 (서버 다운)
        if (!error.response) {
            console.error('서버 연결 실패 - 네트워크 에러 또는 서버 다운');
            toast.error('🚨 서버에 연결할 수 없습니다. 서버 상태를 확인해주세요.');

            // 서버 상태 확인 알림
            const serverStatus = {
                auth: config.AUTH_URL,
                api: config.API_URL
            };

            // 서버 상태 체크 (비동기)
            checkServerStatus(serverStatus);

            throw new Error('서버 연결 실패');
        }

        // 401 에러인 경우 로그인 페이지로 리다이렉트
        if (error.response && error.response.status === 401) {
            console.warn('Unauthorized access, redirecting to login');
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('id_token');
            window.location.href = '/authentication/simple/login';
        }

        // 500 에러인 경우 서버 내부 오류
        if (error.response && error.response.status >= 500) {
            toast.error('🔧 서버 내부 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        }

        throw error;
    }
};


// API 인증키  정보 변경 API
export const updateApiKeyInfo = async (apikeyUuid, data) => {
    // 인터셉터에서 자동 처리 (횡단관심사)
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.put(`/apikeys/${apikeyUuid}`, data, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('API 인증키 정보 업데이트 에러:');
        throw error;
    }
};

// // 인증키 삭제 API 
export const deleteApiKeyInfo = async (apikeyUuid) => {
    // 중요한 작업: 키 삭제는 OIDC 재인증 필요
    const headers = getTokenHeaders('/safedb/common/keys/delete');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.delete(`/apikeys/${apikeyUuid}`, {
            headers
        });
        return response; // API 응답 데이터를 반환
    } catch (error) {
        console.log("인증키 삭제 API  에러 발생");
        throw error;
    }
};

// API 인증키 UUID 재발급 API
export const remakeApiKeyInfo = async (apikeyUuid) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.put(`/apikeys/${apikeyUuid}/reissuance`, {}, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('API 키 재발급 에러:');
        throw error;
    }
};
// API 인증키 추가 API
export const addApiKey = async (data) => {
    // 인터셉터에서 자동 처리 (횡단관심사)
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.post('/apikeys', data, {
            headers
        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('그룹목록 조회 에러:');
        throw error;
    }
};




// // 그룹관리 목록 조회 API 
export const fetchGroupList = async (page = 0, size = 10) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/groups', {
            params: { page, size },
            headers

        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('그룹목록 조회 에러:');
        throw error;
    }
};

// // 그룹관리 항목 삭제 API 
export const deleteGroupList = async (apigroup_uuid) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.delete(`/groups/${apigroup_uuid}`, {

            headers

        });

        return response; // API 응답 데이터를 반환
    } catch (error) {
        console.log("그룹관리 항목 삭제 API 에러 발생");
        throw error;
    }
};

// // 그룹 목록 단순 조회 API 
export const SelectGroupList = async () => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/groups/list', {
            headers

        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('그룹 목록 단순 조회 에러:');
        throw error;
    }
};


// 그룹 등록 API
export const addGroup = async ({ groupName, groupDescription }) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.post('/groups', {
            groupName,
            groupDescription
        }, { headers });
        return response.data; // {boolean} 타입의 응답을 반환
    } catch (error) {
        console.error('그룹 추가 에러:');
        throw error;
    }
};







// // 암호키 목록 조회 API 
export const fetchEncKeyList = async (page = 0, size = 10) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/policy', {
            params: { page, size },
            headers

        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('암호키 목록 조회 에러:');
        throw error;
    }
};

// // 암호키 삭제 API 
export const deleteEncKey = async (policyUuid) => {
    // 중요한 작업: 키 삭제는 OIDC 재인증 필요
    const headers = getTokenHeaders('/safedb/common/keys/delete');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.delete(`/policy/${policyUuid}`, {
            headers
        });

        return response; // API 응답 데이터를 반환
    } catch (error) {
        console.log("암호키 삭제 API 에러 발생");
        throw error;
    }
};

// // 암호키 상세 조회 API 
export const detailEncKey = async (policyUuid) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.get(`/policy/${policyUuid}`, {
            headers
        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('암호키 상세 조회 에러:');
        throw error;
    }
};

// // 암호키 등록 세팅 값 조회 API 
export const settingEncKey = async () => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.get('/policy/settings', {
            headers
        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('암호키 세팅 값 조회 에러:');
        throw error;
    }
};


// // 암호키 상세내 적용대상목록 조회 API 
// export const fetchEncPolicyList = async (policyUuid) => {
//     // 기본 헤더 구성
//     const headers = {};

//     // siteToken이 존재하면 사용하고, 없으면 token을 사용
//     const siteToken = sessionStorage.getItem('siteToken');
//     const token = sessionStorage.getItem('token');
//     if (siteToken) {
//         headers.Authorization = `Bearer ${siteToken}`;
//     } else if (token) {
//         headers.Authorization = `Bearer ${token}`;
//     }
//     try {
//         const response = await api.get(`/policy/${policyUuid}/apikeys`, {
//             headers
//         });
//         //console.log(response);
//         return response.data; // API 응답 데이터를 반환
//     } catch (error) {
//         console.error('암호키 상세 적용대상목록 조회 에러:');
//         throw error;
//     }
// };
//저장된 정책 상세 조회
export const fetchEncPolicyList = async (policyUuid, page = 0, size = 10) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get(`/policy/${policyUuid}/apikeys`, {
            params: { page, size }, // 페이지네이션 파라미터 추가
            headers
        });

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("저장된 정책 상세 조회 에러 발생");
        throw error;
    }
};

// // 암호키 등록 시  적용대상목록 조회 API 
export const addEncPolicyList = async () => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.get('policy/settings/apikeys', {
            headers
        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("적용대상목록 조회 API 에러 발생");
        throw error;
    }
};

// 암호화 키 정책 변경 API
export const updateEncryptKeyPolicy = async (policyUuid, apikeyPolicies) => {
    // 인터셉터에서 자동 처리 (횡단관심사)
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.put(`/policy/${policyUuid}`, { apikeyPolicies }, {
            headers
        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('암호화 키 정책 변경 에러:');
        throw error;
    }
};

// 암호화 키 정책 추가 API
export const addEncryptKeyPolicy = async (policyData) => {
    // 인터셉터에서 자동 처리 (횡단관심사)
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.post('/policy', policyData, {
            headers
        });

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("암호화 키 정책 추가 API 에러 발생");
        throw error;
    }
};

//////////////////////////////////////////////////////////////////////////////////////// 공유키 관련

// // 공유키 목록 조회 API (암호키 등록 사용)
export const showPubKeyList = async () => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.get('policy/shared/list', {

            headers
        });

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("공유키 목록 조회 API 에러 발생");
        throw error;
    }
};

// // 공유키 목록 조회 API 
export const fetchPubKeyList = async (page = 0, size = 10) => {
    // 데모모드: mock 데이터 반환
    if (isDemoMode()) {
        return mockPubKeyList(page, size);
    }

    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.get('policy/shared', {
            params: { page, size },
            headers
        });

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("공유키 목록 조회 API 에러 발생");
        throw error;
    }
};


// 공유키 등록 API 
export const addPubKey = async (data) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.post('/policy/shared', data, {
            headers
        });

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("공유키 등록 API 에러 발생");
        throw error;
    }
};

// 공유키 변경 API
export const updatePubKey = async (sharedKeyUuid, description) => {
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.put(`/policy/shared/${sharedKeyUuid}`, { description }, {
            headers
        });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('공유키 변경 에러:');
        throw error;
    }
};

// // 공유키 삭제 API 
export const deletePubKey = async (sharedKeyUuid) => {
    // 중요한 작업: 키 삭제는 OIDC 재인증 필요
    const headers = getTokenHeaders('/safedb/common/keys/delete');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.delete(`/policy/shared/${sharedKeyUuid}`, {
            headers
        });

        return response; // API 응답 데이터를 반환
    } catch (error) {
        console.log("공유키 삭제 API 에러 발생");
        throw error;
    }
};
//////////////////////////////////////////////////////////////////////////////////////// 대시보드 관련
// 고객사  대시보드 요약
export const fetchSummaryCounts = async ({ period, start, end, timeZone, type, demo }) => {
    // 데모모드: mock 데이터 반환
    if (demo) {
        return mockSummaryCounts(period, type);
    }
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.post('/chart/allCnt',
            { period, start, end, timeZone, type, demo },
            {
                headers
            }
        );

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("고객사  대시보드 요약 에러 발생");
        throw error;
    }
};
// 고객사 대시보드 지난 처리량
export const fetchDataSize = async ({ period, start, end, timeZone, type, demo }) => {
    // 데모모드: mock 데이터 반환
    if (demo) {
        return mockDataSize(period, type);
    }
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.post('/chart/dataSize',
            { period, start, end, timeZone, type, demo },
            {
                headers
            }
        );
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.log("고객사 대시보드 지난 처리량 에러 발생");
        throw error;
    }
};

// 고객사 대시보드 지난 암복호화 요청량
export const fetchRecentlyEncDec = async ({ period, start, end, timeZone, type, demo }) => {
    // 데모모드: mock 데이터 반환
    if (demo) {
        return mockRecentlyEncDec(period, type);
    }
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.post('/chart/encdecCnt',
            { period, start, end, timeZone, type, demo },
            {
                headers
            }
        );
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('요약 데이터 조회 에러:');
        throw error;
    }
};

// 고객사 대시보드 인증키 타입별 요청 비교
export const fetchKeyTypeComp = async ({ period, start, end, timeZone, demo }) => {
    // 데모모드: mock 데이터 반환
    if (demo) {
        return mockKeyTypeComp();
    }
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.post('/chart/type',
            { period, start, end, timeZone, demo },
            {
                headers
            }
        );
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('요약 데이터 조회 에러:');
        throw error;
    }
};

// 고객사 대시보드 알고리즘 요청 비교
export const fetchAlgoComp = async ({ period, start, end, timeZone, type, demo }) => {
    // 데모모드: mock 데이터 반환
    if (demo) {
        return mockAlgoComp(type);
    }
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.post('/chart/algo',
            { period, start, end, timeZone, type, demo },
            {
                headers
            }
        );
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('요약 데이터 조회 에러:');
        throw error;
    }
};

// 고객사 대시보드 암호키 요청량 비교
export const fetchEncKeyComp = async ({ period, start, end, timeZone, order, type, demo }) => {
    // 데모모드: mock 데이터 반환
    if (demo) {
        return mockEncKeyComp(type, order);
    }
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.post('/chart/topPolicy',
            { period, start, end, timeZone, order, type, demo },
            {
                headers
            }

        );
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('요약 데이터 조회 에러:');
        throw error;
    }
};


// 고객사 대시보드 6개월간 요금 청구량
export const fetchSixMonthPay = async ({ demo } = {}) => {
    // 데모모드: mock 데이터 반환
    if (demo) {
        return mockSixMonthPay();
    }
    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    try {
        const response = await api.post('/chart/billing', { demo }, { headers });
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('요약 데이터 조회 에러:');
        throw error;
    }
};

//////////////////////////////////////////////////////////////////////////////////////// 총괄대시보드 관련
// 총괄대시보드 요약 차트
export const allReqSummary = async ({ period, start, end, timeZone, demo }) => {
    // 기본 헤더 구성
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/allReqSummary',
            { period, start, end, timeZone, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]요약 데이터 조회 에러:');
        throw error;
    }
};

// 고객사 이상 순위 차트
export const anomaliesRank = async ({ period, start, end, timeZone, type, demo }) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/anomaliesRank',
            { period, start, end, timeZone, type, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]고객사 이상 순위 차트 데이터 조회 에러:');
        throw error;
    }
};

// 고객사 활동 순위 차트
export const activeRank = async ({ period, start, end, timeZone, type, demo }) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/activeRank',
            { period, start, end, timeZone, type, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]고객사 활동 순위 차트 데이터 조회 에러:');
        throw error;
    }
};

// 평균 요청량 순위 차트
export const avgRequest = async ({ period, start, end, timeZone, type, demo }) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/avgRequest',
            { period, start, end, timeZone, type, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]평균 요청량 순위 차트 데이터 조회 에러:');
        throw error;
    }
};


// 일별 최대 처리량 시간 분포  차트
export const timeDistribution = async ({ period, start, end, timeZone, type, demo }) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/timeDistribution',
            { period, start, end, timeZone, type, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]평균 요청량 순위 차트 데이터 조회 에러:');
        throw error;
    }
};

// 시간별 평균 처리 속도  차트
export const hourlyAvg = async ({ period, start, end, timeZone, type, demo }) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/hourlyAvg',
            { period, start, end, timeZone, type, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]시간별 평균 요청량 순위 차트 데이터 조회 에러:');
        throw error;
    }
};



// 고객사별 암호키 유휴율 차트
export const policyPerRank = async ({ period, start, end, timeZone, demo }) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/policyPerRank',
            { period, start, end, timeZone, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]고객사별 암호키 유휴율 차트 데이터 조회 에러:');
        throw error;
    }
};



// 고객사별 암호키 유휴 기간 차트
export const policyPeriodRank = async ({ period, start, end, timeZone, demo }) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/policyPeriodRank',
            { period, start, end, timeZone, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]고객사별 암호키 유휴 기간 차트 데이터 조회 에러:');
        throw error;
    }
};

export const totalAlgoRequest = async ({ period, start, end, timeZone, demo }) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.post('/inichart/totalAlgoRequest',
            { period, start, end, timeZone, demo },
            { headers });
        return response.data;
    } catch (error) {
        console.error('[SA]전체 알고리즘별 요청량 차트 데이터 조회 에러:');
        throw error;
    }
};

//////////////////////////////////////////////////////////////////////////////////////// 모니터링 관련
// // 관리 로그 조회 API 
// export const fetchManageLog = async (page = 0, size = 10, data) => {
//     // 기본 헤더 구성
//     const headers = {};

//     // siteToken이 존재하면 사용하고, 없으면 token을 사용
//     const siteToken = sessionStorage.getItem('siteToken');
//     const token = sessionStorage.getItem('token');
//     if (siteToken) {
//         headers.Authorization = `Bearer ${siteToken}`;
//     } else if (token) {
//         headers.Authorization = `Bearer ${token}`;
//     }

//     try {
//         const response = await api.post('/logs/management', {
//             params: { page, size, data },
//             headers: {
//                 Authorization: `Bearer ${sessionStorage.getItem('token')}` // 토큰을 세션 스토리지에서 가져와 헤더에 포함
//             }

//         });
//         //console.log("관리로그", response);
//         return response.data; // API 응답 데이터를 반환
//     } catch (error) {
//         console.error('관리로그 조회 에러:');
//         throw error;
//     }
// };

export const fetchManageLog_Admin = async ({
    page = 0,
    size = 10,
    searchStartTime,
    searchEndTime,
    timeZone,
    userId,
    companyId,
    userRole,
    taskHistory,
    result,
    insertTime = 'desc'
}) => {
    const headers = {};

    // 토큰 설정 (siteToken 우선 사용)

    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    // API 요청 본문 구성
    const requestBody = {
        searchStartTime,
        searchEndTime,
        timeZone,
        userId,
        companyId,
        userRole,
        taskHistory,
        result,
        order: {
            'insertTime': insertTime
        },
    };
    const params = { page, size };

    //console.log("관리 로그 요청 본문:", JSON.stringify(requestBody, null, 2)); // 요청 본문 출력

    try {
        // API 호출
        const response = await api.post('/logs/management', requestBody, {
            headers,
            params
        });

        //console.log('관리 로그 데이터:', response.data);
        return response.data; // API 응답 데이터 반환
    } catch (error) {
        console.error('관리 로그 조회 에러:');
        throw error;
    }
};

export const fetchManageLog = async ({
    page = 0,
    size = 10,
    searchStartTime,
    searchEndTime,
    timeZone,
    userId,
    userRole,
    taskHistory,
    result,
    insertTime = 'desc'
}) => {
    // 데모모드: mock 데이터 반환
    if (isDemoMode()) {
        return mockManageLog(page, size);
    }

    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    // API 요청 본문 구성
    const requestBody = {
        searchStartTime,
        searchEndTime,
        timeZone,
        userId,
        userRole,
        taskHistory,
        result,
        order: {
            'insertTime': insertTime
        },
    };
    const params = { page, size };

    //console.log("관리 로그 요청 본문:", JSON.stringify(requestBody, null, 2)); // 요청 본문 출력

    try {
        // API 호출
        const response = await api.post('/logs/management', requestBody, {
            headers,
            params
        });

        //console.log('관리 로그 데이터:', response.data);
        return response.data; // API 응답 데이터 반환
    } catch (error) {
        console.error('관리 로그 조회 에러:');
        throw error;
    }
};

// 청구 내역 조회 API
export const fetchBillingHistory = async (params) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/billing/history', {
            params,
            headers
        });
        return response.data;
    } catch (error) {
        console.error('청구 내역 조회 에러:', error);
        throw error;
    }
};

export const fetchBillingData = async (zoneId) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/billing/data', {
            headers,
            params: { zoneId }
        });
        return response.data;
    } catch (err) {
        console.error('청구 데이터 조회 에러:', err);
        throw err;
    }
};

export const fetchRealTimeData = async (params = {}, zoneId, serviceType = 'sdk') => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null;
    }

    const queryParams = { ...params };
    if (zoneId) queryParams.zoneId = zoneId;
    if (serviceType) queryParams.serviceType = serviceType;

    try {
        const response = await api.get('/billing/realtime', {
            headers,
            params: queryParams
        });
        return response.data;
    } catch (err) {
        console.error('실시간 데이터 조회 에러:', err);
        throw err;
    }
};

export const fetchTodayHourData = async (zoneId, serviceType = 'sdk') => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null;
    }

    const params = {};
    if (zoneId) params.zoneId = zoneId;
    if (serviceType) params.serviceType = serviceType;

    try {
        const response = await api.get('/billing/today', {
            headers,
            params // API 호출 시 쿼리 파라미터로 전달
        });
        return response.data;
    } catch (err) {
        console.error('오늘 데이터 조회 에러:', err);
        throw err;
    }
};

export const fetchPeriodData = async (zoneId, usageRange, serviceType = 'sdk') => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null;
    }

    const params = {};
    if (zoneId) params.zoneId = zoneId;
    if (usageRange) params.usageRange = usageRange;
    if (serviceType) params.serviceType = serviceType;

    try {
        const response = await api.get('/billing/period', {
            headers,
            params
        });
        return response.data;
    } catch (err) {
        console.error('기간 데이터 조회 에러:', err);
        throw err;
    }
};

export const fetchBillingDetail = async (yearMonth, zoneId) => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/billing/detail', {
            headers,
            params: { yearMonth, zoneId } // API 호출 시 쿼리 파라미터로 전달
        });
        return response.data;
    } catch (err) {
        console.error('청구 상세 데이터 조회 에러:', err);
        throw err;
    }
};

// 알고리즘 목록 조회 API 호출 함수
export const fetchAvailableAlgorithms = async () => {
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/algorithms/availableNames', {
            headers
        });
        return response.data.data;
    } catch (error) {
        console.error('알고리즘 목록 조회 API 에러:', error);
        throw error;
    }
};

// // 암복호화 로그 조회 API 
export const fetchEncDecLog = async ({
    page = 0,
    size = 10,
    searchStartTime,
    searchEndTime,
    timeZone,
    policyName,
    algo,
    cryptoMethod,
    startTime = 'desc',
    endTime = 'desc',
    requestTotalCnt = 'desc',
    successCnt = 'desc',
    failCnt = 'desc'
}) => {
    // 데모모드: mock 데이터 반환
    if (isDemoMode()) {
        return mockEncDecLog(page, size);
    }

    // 기본 헤더 구성
    const headers = getTokenHeaders('/safedb/common/');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }
    const requestBody = {
        searchStartTime,
        searchEndTime,
        timeZone,
        policyName,
        algo,
        cryptoMethod,
        order: {
            'startTime': startTime,
            'endTime': endTime,
            'requestTotalCnt': requestTotalCnt,
            'successCnt': successCnt,
            'failCnt': failCnt,
        },
    };
    const params = { page, size };

    try {
        const response = await api.post('/logs/crypto', requestBody, {
            headers,
            params
        });

        // console.log("암복호화 로그", response);
        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('암복호화 조회 에러:');
        throw error;
    }
};

////////////////////////// 배포 부분

// // API KEY 목록 조회 API 
export const DownloadSDK = async (page = 0, size = 10) => {
    // 중요한 작업: SDK 다운로드는 OIDC 재인증 필요
    const headers = getTokenHeaders('/safedb/common/keys/export');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get('/download/sdk', {
            params: { page, size },
            headers
        });

        return response.data; // API 응답 데이터를 반환
    } catch (error) {
        console.error('SDK 목록 조회 에러:');
        throw error;
    }
};


/////////////////////////////////////////////////////////////////////////////////////// 토큰 유지관련
// 요청 인터셉터 (토큰 만료 사전 체크 포함)
api.interceptors.request.use(
    async (config) => {
        // 🔒 중요 작업 감지는 백엔드 RequestInterceptor에서 처리
        // 프론트엔드는 401 응답 시 자동 재인증만 담당

        // 중요 작업 감지는 백엔드에서 처리하므로 이 부분 제거됨
        // 백엔드에서 401 + X-Require-Fresh-Auth 헤더를 받으면 응답 인터셉터에서 처리

        const siteToken = sessionStorage.getItem('siteToken');
        const token = sessionStorage.getItem('token');

        // 토큰 만료 사전 체크 및 갱신 (siteToken 사용 중에도 수행) - 중복 방지 적용
        if (token && TokenManager.isTokenExpiringsoon('token')) {
            const now = Date.now();

            // 인터셉터에서도 중복 갱신 방지
            if (!TokenManager._refreshInProgress && (now - TokenManager._lastRefreshTime) > TokenManager._refreshCooldown) {
                TokenManager._refreshInProgress = true;
                TokenManager._lastRefreshTime = now;

                try {
                    // 백그라운드 갱신 여부 전달 (siteToken 사용 중인지로 판단)
                    await TokenManager.refreshToken(!!siteToken);
                } catch (error) {
                    // 갱신 실패는 기존 토큰으로 계속 진행
                    // (응답 인터셉터에서 401 처리)
                } finally {
                    TokenManager._refreshInProgress = false;
                }
            }
        }

        const refreshedToken = sessionStorage.getItem('token');

        // siteToken이 없을 때만 일반 token을 설정
        if (!siteToken && refreshedToken) {
            config.headers.Authorization = `Bearer ${refreshedToken}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// 응답 인터셉터 (개선된 토큰 갱신 로직)
api.interceptors.response.use(
    async (response) => {
        // 202 Accepted + 챌린지 토큰 처리 (중요 작업)
        try {
            if (response && response.status === 202) {
                const requireFreshAuth = response.headers && response.headers['x-require-fresh-auth'];
                const challengeToken = response.headers && response.headers['x-challenge-token'];

                if (requireFreshAuth === 'true' && challengeToken) {
                    const originalRequest = response.config || {};
                    if (!originalRequest._retry202) {
                        originalRequest._retry202 = true;

                        // 시스템 관리자 토큰인 경우 OIDC 재인증 우회 (SSO 우회 로그인)
                        if (isCurrentUserSystemAdmin()) {
                            console.log('🔑 System admin token detected - bypassing OIDC re-authentication');
                            // 로컬 관리자는 원래 요청을 그대로 재시도
                            return api(originalRequest);
                        }

                        console.log('🔒 Backend returned 202 requiring fresh OIDC auth. Challenge token:', challengeToken);

                        const oidcToken = localStorage.getItem('oidc_access_token');
                        if (!oidcToken) {
                            throw new Error('No OIDC token available');
                        }

                        const currentSiteToken = sessionStorage.getItem('siteToken');
                        const requestBody = { challengeToken };
                        if (currentSiteToken) {
                            requestBody.siteToken = currentSiteToken;
                            console.log('🔄 Including siteToken in 202 challenge token re-authentication request');
                        }

                        const authResponse = await authApi.post('/auth/oidc/exchange', requestBody, {
                            headers: { Authorization: `Bearer ${oidcToken}` }
                        });

                        if (authResponse.data?.success && authResponse.data?.token) {
                            const wasSiteTokenUsed = !!currentSiteToken && originalRequest.headers?.Authorization?.includes(currentSiteToken);
                            originalRequest.headers = originalRequest.headers || {};

                            if (wasSiteTokenUsed) {
                                console.log('🔄 Maintaining siteToken for impersonation, not saving new JWT to sessionStorage (202)');
                                originalRequest.headers.Authorization = `Bearer ${authResponse.data.token}`;
                            } else {
                                sessionStorage.setItem('token', authResponse.data.token);
                                console.log('🔄 New JWT with challenge token saved to sessionStorage (202)');
                                originalRequest.headers.Authorization = `Bearer ${authResponse.data.token}`;
                            }

                            return api(originalRequest);
                        } else {
                            throw new Error('Failed to get fresh JWT with challenge token from Auth server (202)');
                        }
                    }
                }
            }
        } catch (e) {
            console.error('202 challenge handling failed:', e);
            // 시스템 관리자 토큰인 경우 로그아웃하지 않음
            if (isCurrentUserSystemAdmin()) {
                console.log('🔑 System admin token - not forcing logout on 202 error');
                return Promise.reject(e);
            }
            try { forceLogoutToLogin('중요한 작업을 위해 다시 로그인해 주세요.', 'critical_operation_reauth_failed'); } catch (_) { /* no-op */ }
            return Promise.reject(e);
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // 🔒 백엔드에서 중요 작업에 대한 챌린지 토큰 재인증 요구 처리
        if (error.response && error.response.status === 401) {
            const requireFreshAuth = error.response.headers['x-require-fresh-auth'];
            const challengeToken = error.response.headers['x-challenge-token'];

            if (requireFreshAuth === 'true' && challengeToken && !originalRequest._retry) {
                originalRequest._retry = true;

                // 시스템 관리자 토큰인 경우 OIDC 재인증 우회 (SSO 우회 로그인)
                if (isCurrentUserSystemAdmin()) {
                    console.log('🔑 System admin token detected - bypassing OIDC re-authentication (401)');
                    // 로컬 관리자는 원래 요청을 그대로 재시도
                    return api(originalRequest);
                }

                console.log('🔒 Backend requires fresh OIDC authentication with challenge token:', challengeToken);

                // 챌린지 토큰과 함께 OIDC 재인증을 위해 Auth 서버로 토큰 교환 시도
                try {
                    const oidcToken = localStorage.getItem('oidc_access_token');
                    if (!oidcToken) {
                        throw new Error('No OIDC token available');
                    }

                    // 챌린지 토큰과 siteToken을 포함하여 Auth 서버에 요청
                    const currentSiteToken = sessionStorage.getItem('siteToken');
                    const requestBody = { challengeToken: challengeToken };
                    if (currentSiteToken) {
                        requestBody.siteToken = currentSiteToken;
                        console.log('🔄 Including siteToken in challenge token re-authentication request');
                    }

                    const authResponse = await authApi.post('/auth/oidc/exchange', requestBody, {
                        headers: { Authorization: `Bearer ${oidcToken}` }
                    });

                    if (authResponse.data.success && authResponse.data.token) {
                        // siteToken 사용 중인지 확인 (위에서 이미 가져온 currentSiteToken 재사용)
                        const wasSiteTokenUsed = !!currentSiteToken && originalRequest.headers.Authorization?.includes(currentSiteToken);

                        if (wasSiteTokenUsed) {
                            // 사이트 토큰 사용 중이었다면 일반 JWT를 저장하지 않고 siteToken 유지
                            console.log('🔄 Maintaining siteToken for impersonation, not saving new JWT to sessionStorage');
                            originalRequest.headers.Authorization = `Bearer ${authResponse.data.token}`;
                        } else {
                            // 일반 토큰 사용 중이었다면 새로운 JWT를 저장
                            sessionStorage.setItem('token', authResponse.data.token);
                            console.log('🔄 New JWT with challenge token saved to sessionStorage');
                            originalRequest.headers.Authorization = `Bearer ${authResponse.data.token}`;
                        }

                        console.log('✅ Fresh JWT with challenge token obtained, retrying critical operation');
                        return api(originalRequest);
                    } else {
                        throw new Error('Failed to get fresh JWT with challenge token from Auth server');
                    }
                } catch (authError) {
                    console.error('OIDC re-authentication with challenge token failed:', authError);

                    // 세션 무효화 여부 판단 (401, 403, 'invalid_token' 등)
                    const isSessionInvalidated = authError.response?.status === 401 ||
                        authError.response?.status === 403 ||
                        authError.response?.data?.error === 'invalid_token' ||
                        authError.response?.data?.error === 'unauthorized' ||
                        authError.message?.includes('invalid_token') ||
                        authError.message?.includes('Token validation failed');

                    if (isSessionInvalidated) {
                        console.warn('🚨 OIDC 세션 무효화 감지 - 백엔드 챌린지 토큰 정리 필요');

                        // 백엔드에 챌린지 토큰 정리 요청 (Best Effort)
                        try {
                            const currentUserId = extractUsernameFromJwt(sessionStorage.getItem('token'));
                            if (currentUserId) {
                                // 백엔드 챌린지 토큰 정리 API 호출 (무시해도 됨)
                                fetch(`${process.env.REACT_APP_WEB_URL || 'https://api.safedb.biz'}/internal/clear-challenge-tokens/${currentUserId}`, {
                                    method: 'DELETE',
                                    headers: { 'Content-Type': 'application/json' }
                                }).catch(() => { /* 실패해도 무시 */ });
                            }
                        } catch (cleanupError) {
                            // 정리 요청 실패는 무시
                            console.debug('Challenge token cleanup request failed (ignored):', cleanupError);
                        }

                        forceLogoutToLogin('다른 브라우저에서 로그인되어 세션이 만료되었습니다. 다시 로그인해 주세요.', 'session_invalidated');
                    } else {
                        forceLogoutToLogin('중요한 작업을 위해 다시 로그인해 주세요.', 'critical_operation_reauth_failed');
                    }

                    return Promise.reject(authError);
                }
            }

            // 일반적인 401 처리 (토큰 만료)
            if (!originalRequest._retry) {
                originalRequest._retry = true;

                try {
                    await TokenManager.refreshToken(false); // 일반 갱신 (리다이렉트 허용)

                    // 새로운 토큰으로 원래 요청 재시도
                    const newToken = sessionStorage.getItem('token');
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;

                    return api(originalRequest); // 실패했던 요청 재시도
                } catch (refreshError) {
                    // 모든 토큰 제거 후 로그인 페이지로 이동
                    sessionStorage.removeItem('token');
                    sessionStorage.removeItem('refreshToken');
                    sessionStorage.removeItem('siteToken');
                    window.location.href = '/authentication/simple/login';
                }
            }
        }

        return Promise.reject(error);
    }
);

// 요금정책 목록 조회 API
export const fetchPricingPolicies = async (page = 0, size = 10) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        const response = await api.get('/pricingPolicy', {
            params: { page, size },
            headers
        });
        return response.data;
    } catch (error) {
        console.error('요금정책 목록 조회 에러:', error);
        throw error;
    }
};

// 요금정책 상세 조회 API
export const fetchPricingPolicy = async (pricingPolicyUuid) => {
    const headers = getTokenHeaders('/safedb/SA/pricing');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get(`/pricingPolicy/${pricingPolicyUuid}`, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('요금정책 상세 조회 에러:', error);
        throw error;
    }
};

// 요금정책 버전 정보 조회 API 추가
export const fetchPricingPolicyVersions = async (originalPricingPolicyUuid) => {
    const headers = getTokenHeaders('/safedb/SA/pricing');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        const response = await api.get(`/pricingPolicy/${originalPricingPolicyUuid}/version`, {
            headers
        });
        return response.data;
    } catch (error) {
        console.error('요금정책 버전 정보 조회 에러:', error);
        throw error;
    }
};

// 요금정책 등록/수정 API
export const createPricingPolicy = async (pricingPolicyData) => {
    const headers = getTokenHeaders('/safedb/SA/pricing');
    if (!headers) {
        console.warn('Authentication in progress, API call cancelled');
        return null; // 조용히 중단 (재인증 진행 중)
    }

    try {
        // 수정모드인 경우 (pricingPolicyUuid가 있는 경우)
        if (pricingPolicyData.pricingPolicyUuid) {
            const response = await api.put(`/pricingPolicy/${pricingPolicyData.pricingPolicyUuid}`, pricingPolicyData, {
                headers
            });
            return response.data;
        }
        // 신규 등록인 경우
        else {
            const response = await api.post('/pricingPolicy', pricingPolicyData, {
                headers
            });
            return response.data;
        }
    } catch (error) {
        console.error('요금정책 저장 에러:', error);
        throw error;
    }
};

// 요금정책 서비스 상세 조회 API
export const fetchServiceItems = async (depth, parent = null) => {
    const headers = {};
    const token = sessionStorage.getItem('token');
    headers.Authorization = `Bearer ${token}`;

    try {
        let url = '/pricingPolicy/service';
        const params = {};

        if (depth !== undefined) {
            params.depth = depth;
        }

        if (parent !== null) {
            params.parent = parent;
        }

        const response = await api.get(url, {
            params,
            headers
        });

        return response.data;
    } catch (error) {
        console.error('서비스 항목 조회 에러:', error);
        throw error;
    }
};

export { api, authApi }; // 필요한 곳에서 `api`와 `authApi` 둘 다 사용할 수 있게 export
